import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  StatefulColumnDefinitionSchema,
  StatefulStoredColumnSchema,
} from "./stateful-tables.contract";

/**
 * A data mapping is the named join of a stateful table (the rows), its schema
 * definition (the shape), and optionally a data asset (the source file).
 */

/**
 * Ceiling on a single readRows response. A mapping preview is for eyeballing a
 * table, not for exporting it, and the rows are held in memory on both sides.
 */
export const MAX_READ_ROWS = 1000;

/**
 * Byte budget for the same response, checked as rows are added. Row width is
 * unbounded — a table can have 200 columns of free text — so a row count alone
 * does not bound the payload.
 */
export const MAX_READ_ROWS_BYTES = 4 * 1024 * 1024;

const SlugSchema = z
  .string()
  .min(1)
  .max(255)
  .regex(
    /^[a-z0-9][a-z0-9_-]*$/,
    "Use lowercase letters, digits, hyphens, or underscores, starting with a letter or digit",
  );

const DisplayConfigSchema = z.object({
  keyLabel: z.string().nullable().optional(),
  columnOrder: z.array(z.string()).optional(),
  hiddenColumns: z.array(z.string()).optional(),
});

export type DataMappingDisplayConfigInput = z.infer<typeof DisplayConfigSchema>;

/** Enough of the linked schema definition to name it in the UI. */
const LinkedSchemaDefinitionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  version: z.number(),
});

const LinkedDataAssetSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  currentVersionId: z.uuid().nullable(),
});

const LinkedDataAssetVersionSchema = z.object({
  id: z.uuid(),
  versionNumber: z.number(),
  fileName: z.string(),
  contentType: z.string(),
  createdAt: z.iso.datetime(),
});

const DataMappingSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  statefulTableId: z.uuid(),
  schemaDefinitionId: z.uuid(),
  /** Null means the mapping follows the stateful table's active version. */
  schemaDefinitionVersionId: z.uuid().nullable(),
  dataAssetId: z.uuid().nullable(),
  dataAssetVersionId: z.uuid().nullable(),
  sourceSyncedFrameId: z.uuid().nullable(),
  sourceSyncedAt: z.iso.datetime().nullable(),
  display: DisplayConfigSchema,
  isActive: z.boolean(),
  createdBy: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  schemaDefinition: LinkedSchemaDefinitionSchema.nullable(),
  dataAsset: LinkedDataAssetSchema.nullable(),
  dataAssetVersion: LinkedDataAssetVersionSchema.nullable(),
});

export type DataMappingResponse = z.infer<typeof DataMappingSchema>;

// A data mapping's columns *are* its stateful table's columns, so both
// contracts share one definition rather than restating the vocabulary.
const ColumnSchema = StatefulColumnDefinitionSchema;
const StoredColumnSchema = StatefulStoredColumnSchema;

/** The mapping plus the live state of what it points at. */
const ResolvedDataMappingSchema = z.object({
  dataMapping: DataMappingSchema,
  statefulTable: z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    /** Key columns in key order; more than one is a composite key. */
    keyColumns: z.array(z.string()).min(1),
    columns: z.array(StoredColumnSchema),
    versionNumber: z.number(),
    schemaDefinitionVersionId: z.uuid().nullable(),
  }),
  /** The pinned schema version is behind the table's active one. */
  schemaPinStale: z.boolean(),
  /** The source asset has a newer version than the one last synced. */
  sourceStale: z.boolean(),
  /**
   * Unique indexes from the schema definition, as column names — alternate
   * lookup keys beyond `statefulTable.keyColumns`. Declared manually or found
   * by unique index discovery; advisory (no physical UNIQUE constraint).
   */
  uniqueIndexes: z.array(z.array(z.string())),
  /**
   * The subset enforced as real UNIQUE indexes on the table's physical
   * storage. Ingests that violate one fail their frame. Managed via
   * `setEnforcedUniqueIndexes`.
   */
  enforcedUniqueIndexes: z.array(z.array(z.string())),
});

export type ResolvedDataMappingResponse = z.infer<
  typeof ResolvedDataMappingSchema
>;

/** A row the resolver skipped or merged, with the reason it gives. */
const IngestIssueSchema = z.object({
  /** 1-based row in the source file; -1 for a file-level issue. */
  rowIndex: z.number(),
  column: z.string(),
  value: z.string().nullable(),
  error: z.string(),
});

const IngestResultSchema = z.object({
  frameId: z.uuid(),
  frameNumber: z.number(),
  status: z.enum(["processed", "failed"]),
  resolvedStatus: z
    .enum(["auto_resolved", "needs_review", "partial", "reviewed"])
    .nullable(),
  /**
   * Rows actually stored — distinct, keyable rows. Lower than the record count
   * of the source file whenever rows share a key (later wins) or the key is
   * missing, which is what `issues` explains.
   */
  rowCount: z.number().nullable(),
  columnCount: z.number().nullable(),
  validationErrorCount: z.number(),
  /**
   * Issues tied to a specific row — a row merged into an existing key, or
   * dropped for an incomplete one. These are the only ones that cost records.
   */
  rowIssueCount: z.number(),
  /**
   * Issues about the file as a whole: a duplicate input header, or a schema
   * column no input column matched. They explain the shape of the read, not
   * lost rows, so they must not be counted as records.
   */
  fileIssueCount: z.number(),
  /** The first {@link MAX_REPORTED_INGEST_ISSUES} issues, for display. */
  issues: z.array(IngestIssueSchema),
  failureReason: z.string().nullable(),
});

/** Enough to show the pattern without shipping a 200-row array to the client. */
export const MAX_REPORTED_INGEST_ISSUES = 20;

const SyncOutputSchema = z.object({
  dataMapping: DataMappingSchema,
  ingest: IngestResultSchema,
});

export type DataMappingSyncResponse = z.infer<typeof SyncOutputSchema>;

export const dataMappingContracts = {
  list: oc
    .output(z.object({ dataMappings: z.array(DataMappingSchema) }))
    .route({
      summary: "List data mappings",
      description:
        "Returns the organization's data mappings, each joining a stateful table of rows, its schema definition and an optional source data asset.",
      method: "GET",
    }),

  get: oc
    .input(z.object({ id: z.uuid() }))
    .output(ResolvedDataMappingSchema)
    .route({
      summary: "Get a data mapping",
      description:
        "Returns one data mapping resolved with its stateful table columns, key columns, schema definition, source asset and unique indexes.",
      method: "GET",
    }),

  /**
   * Rows as `key → row`, for the mapping editor and for business logic.
   *
   * `limit` bounds what crosses the wire; the response always reports the
   * table's `total` so a preview can say how much of it is being shown rather
   * than presenting a window as the whole table.
   */
  readRows: oc
    .input(
      z.object({
        id: z.uuid(),
        /** Rows to return, from the start. Omit for every row. */
        limit: z.number().int().min(1).max(MAX_READ_ROWS).optional(),
      }),
    )
    .output(
      z.object({
        rows: z.record(z.string(), z.record(z.string(), z.unknown())),
        /** Rows the table holds, whatever `limit` returned. */
        total: z.number().int().nonnegative(),
        /** True when the returned rows stop short of `total`. */
        hasMore: z.boolean(),
      }),
    )
    .route({
      summary: "Read rows of a data mapping",
      description:
        "Returns a data mapping's rows keyed by row key, optionally limited to the first N, plus the table's total row count and whether more exist.",
      method: "GET",
    }),

  create: oc
    .input(
      z.object({
        name: z.string().min(1).max(255),
        slug: SlugSchema,
        statefulTableId: z.uuid(),
        description: z.string().max(2000).nullable().optional(),
        dataAssetId: z.uuid().nullable().optional(),
        dataAssetVersionId: z.uuid().nullable().optional(),
        display: DisplayConfigSchema.optional(),
        /** Pin the table's current schema version instead of following it. */
        pinSchemaVersion: z.boolean().optional(),
      }),
    )
    .output(z.object({ dataMapping: DataMappingSchema }))
    .route({
      summary: "Create a data mapping",
      description:
        "Admin-only: creates a named mapping over a stateful table with a slug, optional source data asset version, display settings and optional pinned schema version.",
      method: "POST",
    }),

  update: oc
    .input(
      z.object({
        id: z.uuid(),
        name: z.string().min(1).max(255).optional(),
        description: z.string().max(2000).nullable().optional(),
        display: DisplayConfigSchema.optional(),
        isActive: z.boolean().optional(),
        /** Null detaches the source asset. */
        dataAssetId: z.uuid().nullable().optional(),
        dataAssetVersionId: z.uuid().nullable().optional(),
        pinSchemaVersion: z.boolean().optional(),
      }),
    )
    .output(z.object({ dataMapping: DataMappingSchema }))
    .route({
      summary: "Update a data mapping",
      description:
        "Admin-only: changes a mapping's name, description, display settings, active flag, source data asset or version, or schema-version pinning.",
      method: "POST",
    }),

  delete: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Delete a data mapping",
      description: "Admin-only: deletes a data mapping by ID.",
      method: "DELETE",
    }),

  /**
   * Push a new shape: creates a stateful table version, which creates the
   * matching schema-definition version and recasts existing rows onto it.
   */
  updateSchema: oc
    .input(
      z.object({
        id: z.uuid(),
        columns: z.array(ColumnSchema).min(1).max(100),
        keyColumns: z.array(z.string().min(1).max(100)).min(1).max(8),
        /** Move a pinned mapping onto the version this creates. */
        repin: z.boolean().optional(),
      }),
    )
    .output(ResolvedDataMappingSchema)
    .route({
      summary: "Change a data mapping's columns",
      description:
        "Admin-only: sets new columns and key columns, creating a new table and schema version and recasting existing rows; can repin a pinned mapping.",
      method: "POST",
    }),

  /**
   * Declare exactly which unique indexes are enforced as real UNIQUE indexes
   * on the mapping's stateful table. Existing rows must already satisfy
   * every tuple (400 otherwise); afterwards, ingests that violate one fail
   * their frame instead of writing. An empty list removes all enforcement.
   */
  setEnforcedUniqueIndexes: oc
    .input(
      z.object({
        id: z.uuid(),
        /** Column-name tuples, e.g. [["sku"], ["customer_id", "region"]]. */
        uniqueIndexes: z
          .array(z.array(z.string().min(1)).min(1).max(8))
          .max(20),
      }),
    )
    .output(ResolvedDataMappingSchema)
    .route({
      summary: "Enforce unique indexes on a data mapping",
      description:
        "Admin-only: makes the given column combinations real unique constraints on the mapping's table; existing rows must comply, and violating ingests then fail. Empty list removes enforcement.",
      method: "POST",
    }),

  /**
   * Analyze the mapping's rows for column tuples that uniquely identify a
   * row. Analysis only — nothing is stored; the caller reviews `candidates`
   * and confirms them through `setUniqueIndexes`. Skipped (empty proposal)
   * when the table has no frame or is too large.
   */
  discoverUniqueIndexes: oc
    .input(z.object({ id: z.uuid() }))
    .output(
      z.object({
        /** The unique indexes currently stored, as column names. */
        uniqueIndexes: z.array(z.array(z.string())),
        /**
         * The verified set discovery proposes: stored tuples the data still
         * supports plus newly found minimal keys. Empty when `skipped`.
         */
        candidates: z.array(z.array(z.string())),
        /** Stored tuples the current data contradicts — proposed for removal. */
        droppedTuples: z.array(z.array(z.string())),
        skipped: z.boolean(),
        skipReason: z.enum(["no-frame", "too-many-rows"]).nullable(),
        rowCount: z.number().int().nonnegative(),
        /** Columns the search set aside, with why. */
        skippedColumns: z.array(
          z.object({
            column: z.string(),
            reason: z.enum([
              "key-column",
              "has-nulls",
              "constant",
              "over-column-cap",
            ]),
          }),
        ),
      }),
    )
    .route({
      summary: "Discover unique column combinations",
      description:
        "Analyzes a data mapping's rows to propose column combinations that uniquely identify a row and flags stored ones the data contradicts; saves nothing.",
      method: "POST",
    }),

  /**
   * Store the confirmed unique-index set — the write half of the
   * discover → review → confirm flow. Column-name tuples; an empty list
   * clears the stored set.
   */
  setUniqueIndexes: oc
    .input(
      z.object({
        id: z.uuid(),
        uniqueIndexes: z
          .array(z.array(z.string().min(1)).min(1).max(8))
          .max(50),
      }),
    )
    .output(ResolvedDataMappingSchema)
    .route({
      summary: "Save confirmed unique indexes",
      description:
        "Admin-only: stores the reviewed set of unique column combinations for a data mapping; an empty list clears them. Does not enforce them as constraints.",
      method: "POST",
    }),

  /** Push the source asset's contents into the stateful table as a new frame. */
  syncFromSource: oc
    .input(
      z.object({
        id: z.uuid(),
        /** Defaults to the asset's current version. */
        dataAssetVersionId: z.uuid().optional(),
        mode: z.enum(["snapshot", "upsert"]).optional(),
      }),
    )
    .output(SyncOutputSchema)
    .route({
      summary: "Sync a data mapping from its source file",
      description:
        "Admin-only: loads the source data asset's current or chosen version into the mapping's table as a new frame, by snapshot or upsert, returning ingest results.",
      method: "POST",
    }),
} as const;
